import React, { useCallback } from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch } from 'flow/hooks/useRedux';
import BaseButton from 'flow/components/ui-components/BaseButton';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import {
    admitWaitingParticipant,
    denyWaitingParticipant,
} from 'flow/actions/meeting-actions';
import { WaitingParticipant } from './useWaitingParticipants';

interface Props {
    participant: WaitingParticipant;
}

/**
 * One person asking to join, with the verdict in reach (VID-76). Every
 * participant sees these buttons — admitting is not a host privilege here (see
 * VID-24's permission matrix) — and whoever clicks first decides: the server
 * drops the pending admission on the first verdict and ignores the rest.
 *
 * Sits on a raised card rather than the flat hover row the roster uses — it is
 * asking for something, so it should not read as just another participant.
 *
 * BaseButton rather than Button: Button forces a 120px minimum width that two
 * buttons plus a name cannot afford in a 370px panel.
 */
const MeetingWaitingParticipantRow = ({ participant }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const allow = useCallback(
        () => dispatch(admitWaitingParticipant(participant.name)),
        [dispatch, participant.name],
    );
    const deny = useCallback(
        () => dispatch(denyWaitingParticipant(participant.name)),
        [dispatch, participant.name],
    );

    return (
        <div className="mb-1 flex items-center gap-x-3 rounded-lg bg-gray-700 px-2 py-2 text-base">
            {/* No contact to pass: they are not in the roster until they are
                let in, so ContactAvatar falls back to its generic person. */}
            <ContactAvatar size="medium" />
            <span className="min-w-0 flex-1 truncate">
                {participant.displayName}
            </span>
            <BaseButton
                className="shrink-0 rounded-md border border-gray-500 px-3 py-1 text-sm text-white hover:bg-gray-600"
                onClick={deny}
                data-test-id="waiting-participant-deny"
            >
                {i18next.t('videoconference.waiting_room.deny')}
            </BaseButton>
            <BaseButton
                className="shrink-0 rounded-md bg-white px-3 py-1 text-sm font-medium text-gray-800 hover:bg-gray-200"
                onClick={allow}
                data-test-id="waiting-participant-allow"
            >
                {i18next.t('videoconference.waiting_room.allow')}
            </BaseButton>
        </div>
    );
};

// memo, for the same reason as MeetingParticipantRow: the panel re-renders
// whenever its ancestor MeetingVideoCanvas does.
export default React.memo(MeetingWaitingParticipantRow);
